/**
 * Sara Forbush Art — Main JavaScript
 * Vanilla JS, no dependencies, fully modular
 * Version: 1.0.0
 */

'use strict';

// ============================================================
// 1. Scroll Reveal
// ============================================================
const initScrollReveal = () => {
  const reveals = document.querySelectorAll('.reveal');
  if (!reveals.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

  reveals.forEach(el => observer.observe(el));
};

// ============================================================
// 2. Sticky Header Shadow
// ============================================================
const initStickyHeader = () => {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const onScroll = () => {
    header.classList.toggle('scrolled', window.scrollY > 20);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
};

// ============================================================
// 3. Mobile Navigation
// ============================================================
const initMobileNav = () => {
  const toggle = document.querySelector('.mobile-nav-toggle');
  const nav = document.querySelector('.mobile-nav');
  const close = document.querySelector('.mobile-nav__close');
  const overlay = document.querySelector('.cart-overlay');

  if (!toggle || !nav) return;

  const openNav = () => {
    nav.classList.add('open');
    document.body.style.overflow = 'hidden';
  };

  const closeNav = () => {
    nav.classList.remove('open');
    document.body.style.overflow = '';
  };

  toggle.addEventListener('click', openNav);
  if (close) close.addEventListener('click', closeNav);

  // Close on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && nav.classList.contains('open')) closeNav();
  });
};

// ============================================================
// 4. Cart Drawer
// ============================================================
const initCartDrawer = () => {
  const drawer = document.querySelector('.cart-drawer');
  const overlay = document.querySelector('.cart-overlay');
  const openBtns = document.querySelectorAll('[data-open-cart]');
  const closeBtn = document.querySelector('.cart-drawer__close');

  if (!drawer) return;

  const openCart = () => {
    drawer.classList.add('open');
    overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    drawer.setAttribute('aria-hidden', 'false');
  };

  const closeCart = () => {
    drawer.classList.remove('open');
    overlay.classList.remove('active');
    document.body.style.overflow = '';
    drawer.setAttribute('aria-hidden', 'true');
  };

  openBtns.forEach(btn => btn.addEventListener('click', openCart));
  if (closeBtn) closeBtn.addEventListener('click', closeCart);
  if (overlay) overlay.addEventListener('click', closeCart);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('open')) closeCart();
  });
};

// ============================================================
// 5. Add to Cart (AJAX)
// ============================================================
const initAddToCart = () => {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-add-to-cart]');
    if (!btn) return;

    e.preventDefault();
    const variantId = btn.dataset.variantId;
    const quantity = parseInt(btn.closest('form')?.querySelector('[name="quantity"]')?.value || 1);

    if (!variantId) return;

    btn.disabled = true;
    btn.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });

      if (response.ok) {
        await updateCartDrawer();
        openCartDrawer();
        btn.textContent = 'Added!';
        setTimeout(() => {
          btn.textContent = btn.dataset.originalText || 'Add to Cart';
          btn.disabled = false;
        }, 1500);
      }
    } catch (err) {
      console.error('Add to cart error:', err);
      btn.textContent = 'Error — try again';
      btn.disabled = false;
    }
  });
};

const updateCartDrawer = async () => {
  try {
    const response = await fetch('/cart.js');
    const cart = await response.json();

    // Update cart count badge
    const countEls = document.querySelectorAll('.cart-count');
    countEls.forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });

    // Re-render cart drawer body
    const drawerBody = document.querySelector('.cart-drawer__body');
    const drawerFooter = document.querySelector('.cart-drawer__footer');

    if (drawerBody) {
      if (cart.item_count === 0) {
        drawerBody.innerHTML = `
          <div class="cart-drawer__empty">
            <p>Your cart is empty</p>
            <a href="/collections/all" class="btn btn--secondary">Continue Shopping</a>
          </div>`;
        if (drawerFooter) drawerFooter.style.display = 'none';
      } else {
        drawerBody.innerHTML = cart.items.map(item => {
          const imageHtml = item.image
            ? `<img src="${item.image}" alt="${item.product_title}" class="cart-item__image" width="80" height="80" loading="lazy">`
            : '';
          const subscriptionLabel = item.selling_plan_allocation
            ? `<p class="cart-item__subscription-label">&#10003; ${item.selling_plan_allocation.selling_plan.name}</p>`
            : '';
          const variantHtml = !item.selling_plan_allocation && item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-item__variant">${item.variant_title}</p>`
            : '';
          return `
            <div class="cart-item" data-item-key="${item.key}">
              <a href="${item.url}">${imageHtml}</a>
              <div class="cart-item__details">
                <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
                ${subscriptionLabel}${variantHtml}
                <div class="cart-item__qty-row">
                  <button class="cart-item__qty-btn" data-qty-change="-1" data-item-key="${item.key}" aria-label="Decrease quantity">−</button>
                  <span class="cart-item__qty-display">${item.quantity}</span>
                  <button class="cart-item__qty-btn" data-qty-change="1" data-item-key="${item.key}" aria-label="Increase quantity">+</button>
                </div>
                <div class="cart-item__price-row">
                  <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
                  <button class="cart-item__remove" data-remove-item="${item.key}" aria-label="Remove ${item.product_title}">Remove</button>
                </div>
              </div>
            </div>`;
        }).join('');

        // Show/update footer
        if (drawerFooter) {
          drawerFooter.style.display = '';
          const subtotalEl = drawerFooter.querySelector('.cart-subtotal__price');
          if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

          // Update free shipping notice — hide if all items are subscriptions
          const shippingNotice = drawerFooter.querySelector('.cart-shipping-notice');
          if (shippingNotice) {
            const hasNonSubscription = cart.items.some(i => !i.selling_plan_allocation);
            if (!hasNonSubscription) {
              shippingNotice.style.display = 'none';
            } else {
              shippingNotice.style.display = '';
              if (cart.total_price < 7500) {
                const remaining = formatMoney(7500 - cart.total_price);
                shippingNotice.textContent = `Add ${remaining} more for free shipping!`;
              } else {
                shippingNotice.textContent = 'You qualify for free shipping!';
              }
            }
          }
        }
      }
    }

    return cart;
  } catch (err) {
    console.error('Cart update error:', err);
  }
};

const openCartDrawer = () => {
  const drawer = document.querySelector('.cart-drawer');
  const overlay = document.querySelector('.cart-overlay');
  if (drawer) {
    drawer.classList.add('open');
    overlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
  }
};

// ============================================================
// 6. Quantity Selector
// ============================================================
const initQuantitySelectors = () => {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.quantity-selector__btn');
    if (!btn) return;

    const input = btn.closest('.quantity-selector')?.querySelector('.quantity-selector__input');
    if (!input) return;

    const current = parseInt(input.value) || 1;
    const isPlus = btn.dataset.action === 'plus';

    input.value = isPlus ? current + 1 : Math.max(1, current - 1);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
};

// ============================================================
// 7. Marquee Animation
// ============================================================
const initMarquee = () => {
  const marquees = document.querySelectorAll('.marquee__track');
  marquees.forEach(track => {
    // Clone children for seamless loop
    const items = track.innerHTML;
    track.innerHTML = items + items;
  });
};

// ============================================================
// 8. Product Image Gallery (Product Page)
// ============================================================
const initProductGallery = () => {
  const gallery = document.querySelector('.product-gallery');
  if (!gallery) return;

  const mainImage = gallery.querySelector('.product-gallery__main img');
  const thumbs = gallery.querySelectorAll('.product-gallery__thumb');

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      // Use full-res URLs stored in data attributes, not the thumbnail img src
      const fullSrc = thumb.dataset.fullSrc || thumb.querySelector('img').src;
      const fullSrcset = thumb.dataset.fullSrcset || thumb.querySelector('img').srcset;
      mainImage.src = fullSrc;
      mainImage.srcset = fullSrcset || '';

      thumbs.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
    });
  });
};

// ============================================================
// 9. Variant Selector (Product Page)
// ============================================================
const initVariantSelector = () => {
  const form = document.querySelector('.product-form');
  if (!form) return;

  const selects = form.querySelectorAll('.variant-option');
  // Price element is outside the form — scope to the product section wrapper
  const priceWrapper = document.getElementById('product-price');
  const atcBtn = form.querySelector('[data-add-to-cart]');
  const hiddenIdInput = form.querySelector('input[name="id"]');

  const updateVariant = () => {
    // Build selected options array from all variant-option inputs
    const selectedOptions = Array.from(selects).map(s => {
      if (s.type === 'radio') {
        // For radio groups, find the checked one in this group
        const checked = form.querySelector(`input[name="${s.name}"]:checked`);
        return checked ? checked.value : s.value;
      }
      return s.value;
    });

    // Deduplicate: get unique option values in order (one per option name)
    const seen = new Set();
    const uniqueOptions = [];
    Array.from(selects).forEach(s => {
      if (!seen.has(s.name)) {
        seen.add(s.name);
        if (s.type === 'radio') {
          const checked = form.querySelector(`input[name="${s.name}"]:checked`);
          uniqueOptions.push(checked ? checked.value : s.value);
        } else {
          uniqueOptions.push(s.value);
        }
      }
    });

    const variantData = JSON.parse(document.querySelector('#product-variants-data')?.textContent || '[]');

    const matchedVariant = variantData.find(v =>
      v.options.every((opt, i) => opt === uniqueOptions[i])
    );

    if (matchedVariant) {
      // Update hidden form ID input
      if (hiddenIdInput) hiddenIdInput.value = matchedVariant.id;

      if (atcBtn) {
        atcBtn.dataset.variantId = matchedVariant.id;
        atcBtn.disabled = !matchedVariant.available;
        atcBtn.textContent = matchedVariant.available ? (atcBtn.dataset.originalText || 'Add to Cart') : 'Sold Out';
      }
      // Update price display (handles both sale and regular price)
      if (priceWrapper) {
        if (matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price) {
          priceWrapper.innerHTML = `<span class="product-price__current product-price__current--sale">${formatMoney(matchedVariant.price)}</span><span class="product-price__compare">${formatMoney(matchedVariant.compare_at_price)}</span>`;
        } else {
          priceWrapper.innerHTML = `<span class="product-price__current">${formatMoney(matchedVariant.price)}</span>`;
        }
      }

      // Update active state on size-option labels
      const radioGroups = {};
      selects.forEach(s => {
        if (s.type === 'radio') {
          if (!radioGroups[s.name]) radioGroups[s.name] = [];
          radioGroups[s.name].push(s);
        }
      });
      Object.values(radioGroups).forEach(group => {
        group.forEach(radio => {
          const label = radio.closest('label');
          if (label) label.classList.toggle('active', radio.checked);
        });
      });
    }
  };

  selects.forEach(select => {
    select.addEventListener('change', updateVariant);
  });

  // Run once on init to sync state
  updateVariant();
};

// ============================================================
// 10. Utility: Format Money
// ============================================================
const formatMoney = (cents) => {
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(dollars);
};

// ============================================================
// 11. Newsletter Form
// ============================================================
const initNewsletterForm = () => {
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('.newsletter-form');
    if (!form) return;

    e.preventDefault();
    const email = form.querySelector('input[type="email"]').value;
    const btn = form.querySelector('button[type="submit"]');
    const successMsg = form.querySelector('.newsletter-success');

    btn.disabled = true;
    btn.textContent = 'Subscribing...';

    // Shopify handles newsletter via customer create
    try {
      const response = await fetch('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          form_type: 'customer',
          utf8: '✓',
          'contact[email]': email,
          'contact[tags]': 'newsletter'
        })
      });

      if (successMsg) {
        form.querySelector('.newsletter-form__fields').style.display = 'none';
        successMsg.style.display = 'block';
      }
    } catch (err) {
      btn.textContent = 'Subscribe';
      btn.disabled = false;
    }
  });
};

// ============================================================
// 12. Search Toggle
// ============================================================
const initSearch = () => {
  const searchBtn = document.querySelector('[data-open-search]');
  const searchDrawer = document.querySelector('.search-drawer');
  const searchClose = document.querySelector('.search-drawer__close');
  const searchInput = searchDrawer?.querySelector('input');

  if (!searchBtn || !searchDrawer) return;

  searchBtn.addEventListener('click', () => {
    searchDrawer.classList.add('open');
    document.body.style.overflow = 'hidden';
    setTimeout(() => searchInput?.focus(), 100);
  });

  if (searchClose) {
    searchClose.addEventListener('click', () => {
      searchDrawer.classList.remove('open');
      document.body.style.overflow = '';
    });
  }
};

// ============================================================
// 12b. Quick View Modal
// ============================================================
const initQuickView = () => {
  const modal = document.getElementById('quick-view-modal');
  const inner = document.getElementById('quick-view-inner');
  const closeBtn = document.getElementById('quick-view-close');
  const backdrop = document.getElementById('quick-view-backdrop');

  if (!modal) return;

  const openModal = () => {
    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    // Reset inner after transition
    setTimeout(() => {
      inner.innerHTML = '<div class="quick-view-modal__loading" id="quick-view-loading">Loading&hellip;</div>';
    }, 300);
  };

  closeBtn.addEventListener('click', closeModal);
  backdrop.addEventListener('click', closeModal);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('open')) closeModal();
  });

  // Delegate click on any quick-view button
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-view]');
    if (!btn) return;

    e.preventDefault();
    const handle = btn.dataset.productHandle;
    if (!handle) return;

    openModal();

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error('Product not found');
      const product = await res.json();

      const variant = product.variants[0];
      const image = product.images[0] || '';
      const price = formatMoney(variant.price);
      const comparePrice = variant.compare_at_price && variant.compare_at_price > variant.price
        ? `<s style="opacity:0.5;margin-left:0.6rem">${formatMoney(variant.compare_at_price)}</s>`
        : '';
      const available = variant.available;
      const description = product.description
        ? `<div class="quick-view-modal__description">${product.description.substring(0, 300)}${product.description.length > 300 ? '&hellip;' : ''}</div>`
        : '';

      inner.innerHTML = `
        <div class="quick-view-modal__image">
          ${image ? `<img src="${image}" alt="${product.title}" loading="lazy">` : ''}
        </div>
        <div class="quick-view-modal__content">
          ${product.product_type ? `<p class="quick-view-modal__type">${product.product_type}</p>` : ''}
          <h2 class="quick-view-modal__title">${product.title}</h2>
          <p class="quick-view-modal__price">${price}${comparePrice}</p>
          ${description}
          <div class="quick-view-modal__actions">
            <button
              class="quick-view-modal__atc"
              data-add-to-cart
              data-variant-id="${variant.id}"
              data-original-text="${available ? 'Add to Cart' : 'Sold Out'}"
              ${!available ? 'disabled' : ''}
            >${available ? 'Add to Cart' : 'Sold Out'}</button>
            <a href="/products/${handle}" class="quick-view-modal__view-full">View full details &rarr;</a>
          </div>
        </div>
      `;
    } catch (err) {
      inner.innerHTML = '<div class="quick-view-modal__loading">Unable to load product. <a href="' + (btn.dataset.productUrl || '#') + '">View product &rarr;</a></div>';
    }
  });
};

// ============================================================
// 13. Remove Cart Item
// ============================================================
const initCartQtyUpdate = () => {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;

    const itemKey = btn.dataset.itemKey;
    const change = parseInt(btn.dataset.qtyChange);
    if (!itemKey || isNaN(change)) return;

    // Find current quantity from the display span
    const qtyDisplay = btn.closest('.cart-item__qty-row')?.querySelector('.cart-item__qty-display');
    const currentQty = parseInt(qtyDisplay?.textContent) || 1;
    const newQty = Math.max(0, currentQty + change);

    // Optimistically update display
    if (qtyDisplay) qtyDisplay.textContent = newQty;
    btn.disabled = true;

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: newQty })
      });
      await updateCartDrawer();
      openCartDrawer();
    } catch (err) {
      console.error('Qty update error:', err);
      if (qtyDisplay) qtyDisplay.textContent = currentQty;
      btn.disabled = false;
    }
  });
};

const initRemoveCartItem = () => {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-remove-item]');
    if (!btn) return;

    const itemKey = btn.dataset.removeItem;
    if (!itemKey) return;

    btn.textContent = 'Removing...';
    btn.disabled = true;

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: 0 })
      });
      // Always refresh the drawer to reflect current cart state
      await updateCartDrawer();
      openCartDrawer();
    } catch (err) {
      console.error('Remove item error:', err);
      btn.textContent = 'Remove';
      btn.disabled = false;
    }
  });
};

// ============================================================
// Initialize All Modules
// ============================================================
document.addEventListener('DOMContentLoaded', () => {
  initScrollReveal();
  initStickyHeader();
  initMobileNav();
  initCartDrawer();
  initAddToCart();
  initCartQtyUpdate();
  initRemoveCartItem();
  initQuantitySelectors();
  initMarquee();
  initProductGallery();
  initVariantSelector();
  initNewsletterForm();
  initSearch();
  initQuickView();
});
